import {
  Checkbox,
  Divider,
  InfoLabel,
  makeStyles,
  MessageBar,
  MessageBarBody,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Spinner,
  Text,
  tokens,
  Tooltip,
} from '@fluentui/react-components';
import { Settings20Regular } from '@fluentui/react-icons';
import React from 'react';
import {
  useGetUserPreferencesQuery,
  useUserPreferencesMutation,
} from '../../queries/user';
import { describeApiError } from '../../queries/common';

const useStyles = makeStyles({
  trigger: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingHorizontalS,
    borderRadius: tokens.borderRadiusMedium,
    cursor: 'pointer',
    transition: 'background-color 0.15s ease',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  surface: {
    padding: tokens.spacingVerticalM,
    minWidth: '300px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  headerTitle: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase300,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingVerticalL,
  },
  checkboxLabel: {
    fontSize: tokens.fontSizeBase200,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  infoText: {
    paddingLeft: tokens.spacingHorizontalL,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase100,
  },
  divider: {
    margin: `${tokens.spacingVerticalS} 0`,
  },
});

export const PreferencesPopover = () => {
  const styles = useStyles();
  const { data, isPending: isQueryPending, error } = useGetUserPreferencesQuery();
  const {
    mutate,
    isPending: isSubmitPending,
    error: mutationError,
  } = useUserPreferencesMutation();

  const isLoading = isQueryPending || isSubmitPending;

  // A missing OneDrive permission comes back as consent-required: the mutation
  // then runs the consent flow and replays itself once it is granted.
  const handleOneDriveSyncChange = (enabled: boolean) => {
    mutate({
      autojoin: data?.autojoin || false,
      oneDriveSync: {
        enabled,
      },
    });
  };

  return (
    <Popover withArrow positioning="below-end">
      <PopoverTrigger disableButtonEnhancement>
        <Tooltip content="Préférences" relationship="label">
          <div className={styles.trigger}>
            <Settings20Regular />
          </div>
        </Tooltip>
      </PopoverTrigger>
      <PopoverSurface className={styles.surface}>
        <div className={styles.header}>
          <Settings20Regular />
          <Text className={styles.headerTitle}>Préférences</Text>
        </div>

        {isQueryPending ? (
          <div className={styles.loadingContainer}>
            <Spinner size="small" label="Chargement..." />
          </div>
        ) : error ? (
          <MessageBar intent="error">
            <MessageBarBody>
              {describeApiError(
                error,
                'Erreur lors du chargement des préférences'
              )}
            </MessageBarBody>
          </MessageBar>
        ) : (
          <div className={styles.content}>
            {mutationError && (
              <MessageBar intent="error">
                <MessageBarBody>
                  {describeApiError(
                    mutationError,
                    "Vos préférences n'ont pas pu être enregistrées. Réessayez plus tard."
                  )}
                </MessageBarBody>
              </MessageBar>
            )}

            {/* Autojoin section */}
            <div className={styles.section}>
              <Checkbox
                label={
                  <span className={styles.checkboxLabel}>
                    Autoriser le bot à rejoindre automatiquement mes réunions planifiées
                  </span>
                }
                checked={data?.autojoin || false}
                disabled={isLoading}
                onChange={(_, checkboxData) => {
                  mutate({
                    autojoin: !!checkboxData.checked,
                    oneDriveSync: data?.oneDriveSync,
                  });
                }}
              />
            </div>

            <Divider className={styles.divider} />

            {/* OneDrive sync section */}
            <div className={styles.section}>
              <Checkbox
                label={
                  <InfoLabel
                    info="Synchronise automatiquement vos comptes-rendus et transcriptions vers votre OneDrive personnel dans le dossier Apps/AxysNotes"
                  >
                    <span className={styles.checkboxLabel}>
                      Synchroniser vers OneDrive
                    </span>
                  </InfoLabel>
                }
                checked={data?.oneDriveSync?.enabled || false}
                disabled={isLoading}
                onChange={(_, checkboxData) => {
                  handleOneDriveSyncChange(!!checkboxData.checked);
                }}
              />

              {data?.oneDriveSync?.enabled && (
                <Text className={styles.infoText}>
                  Les fichiers sont synchronisés dans Apps/AxysNotes
                </Text>
              )}
            </div>
          </div>
        )}
      </PopoverSurface>
    </Popover>
  );
};
